import { useCallback, useRef, useState } from 'react';
import type { FormEvent, RefObject } from 'react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}

export interface LoginFormProps {
  email: string;
  password: string;
  rememberMe: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onRememberMeChange: (value: boolean) => void;
  onSubmit: (e: FormEvent) => void;
  isLoading: boolean;
  error: string;
  devicePending: boolean;
  /** Texte du bouton submit */
  submitLabel?: string;
  /** Texte du bouton pendant le chargement */
  loadingLabel?: string;
  /** Ref pour le champ email (focus automatique) */
  emailInputRef?: RefObject<HTMLInputElement | null>;
  /** Ref pour le champ mot de passe */
  passwordInputRef?: RefObject<HTMLInputElement | null>;
}

export function LoginForm({
  email,
  password,
  rememberMe,
  onEmailChange,
  onPasswordChange,
  onRememberMeChange,
  onSubmit,
  isLoading,
  error,
  devicePending,
  submitLabel = 'Se connecter',
  loadingLabel = 'Connexion en cours...',
  emailInputRef,
  passwordInputRef,
}: LoginFormProps) {
  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');

  const internalEmailRef = useRef<HTMLInputElement | null>(null);
  const internalPasswordRef = useRef<HTMLInputElement | null>(null);

  const setInputRef = useCallback(
    (el: HTMLInputElement | null, type: 'email' | 'password') => {
      if (type === 'email') {
        internalEmailRef.current = el;
        if (emailInputRef && 'current' in emailInputRef) {
          (emailInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
        }
      } else {
        internalPasswordRef.current = el;
        if (passwordInputRef && 'current' in passwordInputRef) {
          (passwordInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
        }
      }
    },
    [emailInputRef, passwordInputRef]
  );

  const validate = useCallback((): boolean => {
    let valid = true;

    if (!email.trim()) {
      setEmailError("L'adresse email est requise");
      valid = false;
    } else if (!isValidEmail(email)) {
      setEmailError('Veuillez entrer une adresse email valide');
      valid = false;
    } else {
      setEmailError('');
    }

    if (!password) {
      setPasswordError('Le mot de passe est requis');
      valid = false;
    } else {
      setPasswordError('');
    }

    if (!valid) {
      if (!email.trim() || !isValidEmail(email)) {
        internalEmailRef.current?.focus();
      } else {
        internalPasswordRef.current?.focus();
      }
    }

    return valid;
  }, [email, password]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (isLoading) return;

      if (!validate()) return;

      onSubmit(e);
    },
    [isLoading, validate, onSubmit]
  );

  const handleEmailChange = useCallback(
    (value: string) => {
      onEmailChange(value);
      setEmailError('');
    },
    [onEmailChange]
  );

  const handlePasswordChange = useCallback(
    (value: string) => {
      onPasswordChange(value);
      setPasswordError('');
    },
    [onPasswordChange]
  );

  const inputBaseClass =
    'w-full h-11 px-3 text-sm bg-gray-100 dark:bg-gray-700 rounded-token-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const inputValidClass = 'border border-gray-300 dark:border-gray-600';
  const inputErrorClass = 'border-2 border-red-500';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label
          htmlFor="login-email"
          className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5"
        >
          Adresse email
        </label>
        <input
          ref={el => setInputRef(el, 'email')}
          id="login-email"
          name="email"
          type="email"
          autoComplete="email"
          disabled={isLoading}
          placeholder="exemple@domaine.com"
          value={email}
          onChange={e => handleEmailChange(e.target.value)}
          className={`${inputBaseClass} ${emailError ? inputErrorClass : inputValidClass}`}
          aria-invalid={!!emailError}
          aria-describedby={emailError ? 'login-email-error' : undefined}
        />
        {emailError && (
          <p id="login-email-error" className="text-red-400 text-sm mt-1" role="alert">
            {emailError}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="login-password"
          className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5"
        >
          Mot de passe
        </label>
        <input
          ref={el => setInputRef(el, 'password')}
          id="login-password"
          name="password"
          type="password"
          autoComplete="current-password"
          disabled={isLoading}
          placeholder="••••••••"
          value={password}
          onChange={e => handlePasswordChange(e.target.value)}
          className={`${inputBaseClass} ${passwordError ? inputErrorClass : inputValidClass}`}
          aria-invalid={!!passwordError}
          aria-describedby={passwordError ? 'login-password-error' : undefined}
        />
        {passwordError && (
          <p id="login-password-error" className="text-red-400 text-sm mt-1" role="alert">
            {passwordError}
          </p>
        )}
      </div>

      <div className="flex items-center">
        <input
          id="login-remember"
          name="remember-me"
          type="checkbox"
          checked={rememberMe}
          onChange={e => onRememberMeChange(e.target.checked)}
          disabled={isLoading}
          className="h-4 w-4 rounded border-gray-400 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-primary-500 focus:ring-primary-500 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-800"
        />
        <label htmlFor="login-remember" className="ml-2 text-sm text-gray-600 dark:text-gray-400">
          Se souvenir de moi
        </label>
      </div>

      {devicePending && (
        <div className="rounded-token-lg border border-amber-500/50 bg-amber-50 dark:bg-amber-500/10 p-4">
          <div className="flex gap-3">
            <svg
              className="h-5 w-5 flex-shrink-0 text-amber-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Appareil non autorisé
              </h3>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-100/90">
                Cet appareil n&apos;est pas autorisé. Une demande a été envoyée à votre gérant.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && !devicePending && (
        <div className="rounded-token-lg border border-red-500/50 bg-red-50 dark:bg-red-500/10 p-4">
          <div className="flex gap-3">
            <svg
              className="h-5 w-5 flex-shrink-0 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      <div>
        <button
          type="submit"
          disabled={isLoading || !email || !password}
          className="w-full h-11 flex items-center justify-center gap-2 rounded-token-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-800"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24" aria-hidden>
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {loadingLabel}
            </>
          ) : (
            submitLabel
          )}
        </button>
      </div>
    </form>
  );
}
